import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DownloadCollectionService } from '../service/download-collection.service';
import * as DownloadCollectionActions from './download-collection.actions';
import { switchMap, catchError, of, map } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

type DownloadCollectionAction =
  | ReturnType<typeof DownloadCollectionActions.downloadCollectionSuccess>
  | ReturnType<typeof DownloadCollectionActions.downloadCollectionFailure>;

@Injectable()
export class DownloadCollectionEffects {
  private actions$ = inject(Actions);
  private downloadCollectionService = inject(DownloadCollectionService);

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadCollectionActions.downloadCollection),
      switchMap((action) =>
        this.downloadCollectionService
          .getCollection(action.id, action.language, action.filterValues, action.searchTerm)
          .pipe(
            map((data) => {
              // Blob erstellen und den Download auslösen, aber nicht im Store speichern
              const blob = new Blob([data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'collection.pdf';
              link.click();

              // Bestätigung, keine Blob-Daten im Store
              return DownloadCollectionActions.downloadCollectionSuccess({
                data: 'PDF-Sammlung wurde erfolgreich übertragen. Sie wird im Store nicht gespeichert.',
              });
            }),
            catchError((error: HttpErrorResponse) => {
              // Fehlerbehandlung bei Blob-Fehlern
              if (error.error instanceof Blob && error.error.type === 'application/json') {
                return new Observable<DownloadCollectionAction>((observer) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      const errorJson = JSON.parse(reader.result as string);
                      observer.next(
                        DownloadCollectionActions.downloadCollectionFailure({
                          error: {
                            status: error.status,
                            message: errorJson.message || 'Ein unbekannter Fehler ist aufgetreten.',
                          },
                        })
                      );
                      observer.complete();
                    } catch (e) {
                      observer.next(
                        DownloadCollectionActions.downloadCollectionFailure({
                          error: {
                            status: error.status,
                            message: 'Fehlerdaten konnten nicht verarbeitet werden.',
                          },
                        })
                      );
                      observer.complete();
                    }
                  };
                  reader.onerror = () => {
                    observer.next(
                      DownloadCollectionActions.downloadCollectionFailure({
                        error: {
                          status: error.status,
                          message: 'Fehlerdaten konnten nicht gelesen werden.',
                        },
                      })
                    );
                    observer.complete();
                  };
                  reader.readAsText(error.error); // Blob als Text lesen
                });
              }

              // Fallback: Fehler als nicht-Blob behandeln
              return of(
                DownloadCollectionActions.downloadCollectionFailure({
                  error: {
                    status: error.status,
                    message: 'Ein unbekannter Fehler ist aufgetreten.',
                  },
                })
              );
            })
          )
      )
    )
  );
}
